import { StyleRecord } from "@/storage/indexeddb"
import { bytesToUtf8, utf8ToBytes, XOR } from "@/utils/xor"
import { uuid } from "@/utils/uuid"

// export function encodeStyleOld(style: StyleRecord): Uint8Array {
// 	const json = JSON.stringify(style)

// 	// 256-bit key (32 bytes)
// 	const key = uuid().replace(/-/g, "")

// 	// convert to bytes
// 	const contentBytes = utf8ToBytes(json)
// 	const keyBytes = utf8ToBytes(key)

// 	const encryptedContent = XOR(contentBytes, keyBytes)

// 	// prepend key to encrypted content
// 	const content = new Uint8Array(encryptedContent.length + keyBytes.length)
// 	content.set(keyBytes)
// 	content.set(encryptedContent, keyBytes.length)

// 	return content
// }

// Magic number using special characters that would never appear in a UUID to distinguish from old format
// Using UTF-8 encoding of "🔒📁" (lock and file emoji)
const MAGIC_NUMBER = new Uint8Array([
	0xf0,
	0x9f,
	0x94,
	0x92, // 🔒 (lock emoji)
	0xf0,
	0x9f,
	0x93,
	0x81, // 📁 (file emoji)
])

// Helper to extract binary data from style record
function extractBinaryData(style: StyleRecord): {
	binaryData: Map<string, { type: "float32" | "uint8"; data: ArrayBufferLike }>
	styleWithoutBinary: StyleRecord
} {
	const binaryData = new Map<string, { type: "float32" | "uint8"; data: ArrayBufferLike }>()
	const styleClone = structuredClone(style)

	// Process each reference image
	for (const imageId in styleClone.referenceImages) {
		const record = styleClone.referenceImages[imageId]

		// ---Old binary data fields---
		// Handle deprecated deepCluster feature
		if (record?.features?.deepCluster?.deepClusterFeature) {
			if (record.features.deepCluster.deepClusterFeature instanceof Float32Array) {
				const path = `referenceImages.${imageId}.features.deepCluster.deepClusterFeature`
				binaryData.set(path, {
					type: "float32",
					data: record.features.deepCluster.deepClusterFeature.buffer,
				})
				record.features.deepCluster.deepClusterFeature = { __binaryRef: path } as any
			}
		}
		// Handle scene deepCluster feature
		if (record?.features?.scene?.clusterFeature) {
			if (record.features.scene.clusterFeature instanceof Float32Array) {
				const path = `referenceImages.${imageId}.features.scene.clusterFeature`
				binaryData.set(path, {
					type: "float32",
					data: record.features.scene.clusterFeature.buffer,
				})
				record.features.scene.clusterFeature = { __binaryRef: path } as any
			}
		}

		// ---New binary data fields---

		// Handle new scene deepCluster feature
		if (record?.sceneClusterFeature) {
			if (record.sceneClusterFeature instanceof Float32Array) {
				const path = `referenceImages.${imageId}.sceneClusterFeature`
				binaryData.set(path, {
					type: "float32",
					data: record.sceneClusterFeature.buffer,
				})
				record.sceneClusterFeature = { __binaryRef: path } as any
			}
		}

		// Handle referenceWebpData256x256
		if (record?.referenceWebpData256x256) {
			if (record.referenceWebpData256x256 instanceof Uint8Array) {
				const path = `referenceImages.${imageId}.referenceWebpData256x256`
				binaryData.set(path, {
					type: "uint8",
					data: record.referenceWebpData256x256.buffer,
				})
				record.referenceWebpData256x256 = { __binaryRef: path } as any
			}
		}

		// Handle faceColors
		if (record?.faceColors) {
			if (record.faceColors instanceof Float32Array) {
				const path = `referenceImages.${imageId}.faceColors`
				binaryData.set(path, {
					type: "float32",
					data: record.faceColors.buffer,
				})
				record.faceColors = { __binaryRef: path } as any
			}
		}

		// Handle faceWeights
		if (record?.faceWeights) {
			if (record.faceWeights instanceof Float32Array) {
				const path = `referenceImages.${imageId}.faceWeights`
				binaryData.set(path, {
					type: "float32",
					data: record.faceWeights.buffer,
				})
				record.faceWeights = { __binaryRef: path } as any
			}
		}
	}

	return { binaryData, styleWithoutBinary: styleClone }
}

// Helper to restore binary data to style record
function restoreBinaryData(
	styleWithoutBinary: StyleRecord,
	binaryDataMap: Map<string, { type: "float32" | "uint8"; arrayBuffer: ArrayBufferLike }>,
): StyleRecord {
	const style = styleWithoutBinary

	// Process each reference image
	for (const imageId in style.referenceImages) {
		const record = style.referenceImages[imageId]

		// ---Old binary data fields---
		// Handle deprecated deepCluster feature
		if (
			record?.features?.deepCluster?.deepClusterFeature &&
			typeof record.features.deepCluster.deepClusterFeature === "object" &&
			"__binaryRef" in record.features.deepCluster.deepClusterFeature
		) {
			const path = record.features.deepCluster.deepClusterFeature.__binaryRef as string
			const binaryData = binaryDataMap.get(path)
			if (binaryData) {
				record.features.deepCluster.deepClusterFeature = new Float32Array(
					binaryData.arrayBuffer,
				)
			}
		}
		// Handle scene.clusterFeature
		if (
			record?.features?.scene?.clusterFeature &&
			typeof record.features.scene.clusterFeature === "object" &&
			"__binaryRef" in record.features.scene.clusterFeature
		) {
			const path = record.features.scene.clusterFeature.__binaryRef as string
			const binaryData = binaryDataMap.get(path)
			if (binaryData) {
				record.features.scene.clusterFeature = new Float32Array(binaryData.arrayBuffer)
			}
		}

		// ---New binary data fields---

		// Handle new sceneClusterFeature
		if (
			record?.sceneClusterFeature &&
			typeof record.sceneClusterFeature === "object" &&
			"__binaryRef" in record.sceneClusterFeature
		) {
			const path = record.sceneClusterFeature.__binaryRef as string
			const binaryData = binaryDataMap.get(path)
			if (binaryData) {
				record.sceneClusterFeature = new Float32Array(binaryData.arrayBuffer)
			}
		}

		// Handle referenceWebpData256x256
		if (
			record?.referenceWebpData256x256 &&
			typeof record.referenceWebpData256x256 === "object" &&
			"__binaryRef" in record.referenceWebpData256x256
		) {
			const path = record.referenceWebpData256x256.__binaryRef as string
			const binaryData = binaryDataMap.get(path)
			if (binaryData) {
				record.referenceWebpData256x256 = new Uint8Array(binaryData.arrayBuffer)
			}
		}

		// Handle faceColors
		if (
			record?.faceColors &&
			typeof record.faceColors === "object" &&
			"__binaryRef" in record.faceColors
		) {
			const path = record.faceColors.__binaryRef as string
			const binaryData = binaryDataMap.get(path)
			if (binaryData) {
				record.faceColors = new Float32Array(binaryData.arrayBuffer)
			}
		}

		// Handle faceWeights
		if (
			record?.faceWeights &&
			typeof record.faceWeights === "object" &&
			"__binaryRef" in record.faceWeights
		) {
			const path = record.faceWeights.__binaryRef as string
			const binaryData = binaryDataMap.get(path)
			if (binaryData) {
				record.faceWeights = new Float32Array(binaryData.arrayBuffer)
			}
		}
	}

	return style
}

export function encodeStyle(style: StyleRecord): Uint8Array {
	// Extract binary data from style
	const { binaryData, styleWithoutBinary } = extractBinaryData(style)

	// Encode the style without binary data
	const json = JSON.stringify(styleWithoutBinary)

	// 256-bit key (32 bytes)
	const key = uuid().replace(/-/g, "")

	// Convert to bytes
	const contentBytes = utf8ToBytes(json)
	const keyBytes = utf8ToBytes(key)

	// Encrypt only the style data (not the binary data)
	const encryptedContent = XOR(contentBytes, keyBytes)

	// Create binary data header
	let totalBinarySize = 0

	// Calculate total binary size
	for (const [_, data] of binaryData.entries()) {
		totalBinarySize += data.data.byteLength
	}

	// Create the header JSON
	const binaryHeaderJson = JSON.stringify(
		Array.from(binaryData.entries()).map(([path, data]) => ({
			path,
			type: data.type,
			size: data.data.byteLength,
		})),
	)
	const binaryHeaderBytes = utf8ToBytes(binaryHeaderJson)

	// Calculate total file size and offsets
	const headerSize = binaryHeaderBytes.length
	const encryptedSize = encryptedContent.length
	const keySize = keyBytes.length
	const magicNumberSize = MAGIC_NUMBER.length

	// Structure:
	// [8 bytes: magic number][4 bytes: header size][header][32 bytes: key][encrypted content][binary data]
	const headerSizeBytes = new Uint8Array(4)
	const view = new DataView(headerSizeBytes.buffer)
	view.setUint32(0, headerSize, true) // little-endian

	// Create the final buffer
	const totalSize = magicNumberSize + 4 + headerSize + keySize + encryptedSize + totalBinarySize
	const result = new Uint8Array(totalSize)

	// Write magic number
	result.set(MAGIC_NUMBER, 0)

	// Write header size
	result.set(headerSizeBytes, magicNumberSize)

	// Write header
	result.set(binaryHeaderBytes, magicNumberSize + 4)

	// Write key
	result.set(keyBytes, magicNumberSize + 4 + headerSize)

	// Write encrypted content
	result.set(encryptedContent, magicNumberSize + 4 + headerSize + keySize)

	// Write binary data
	let binaryOffset = magicNumberSize + 4 + headerSize + keySize + encryptedSize
	for (const [_, data] of binaryData.entries()) {
		const buffer = new Uint8Array(data.data)
		result.set(buffer, binaryOffset)
		binaryOffset += buffer.length
	}

	return result
}

export function decodeStyle(buffer: ArrayBuffer): StyleRecord {
	const dataView = new Uint8Array(buffer)

	// Check for magic number to identify the format
	const isMagicNumberMatch =
		dataView.length >= 8 &&
		dataView[0] === MAGIC_NUMBER[0] &&
		dataView[1] === MAGIC_NUMBER[1] &&
		dataView[2] === MAGIC_NUMBER[2] &&
		dataView[3] === MAGIC_NUMBER[3] &&
		dataView[4] === MAGIC_NUMBER[4] &&
		dataView[5] === MAGIC_NUMBER[5] &&
		dataView[6] === MAGIC_NUMBER[6] &&
		dataView[7] === MAGIC_NUMBER[7]

	if (isMagicNumberMatch) {
		// Process new format with binary data separation
		const magicNumberSize = MAGIC_NUMBER.length

		// Read header size (4 bytes after magic number)
		const headerSizeView = new DataView(dataView.buffer, magicNumberSize, 4)
		const headerSize = headerSizeView.getUint32(0, true) // little-endian

		// Read header
		const headerBytes = dataView.subarray(magicNumberSize + 4, magicNumberSize + 4 + headerSize)
		const headerJson = bytesToUtf8(headerBytes)
		const binaryDataInfo = JSON.parse(headerJson) as Array<{
			path: string
			type: "float32" | "uint8"
			size: number
		}>

		// Extract key (32 bytes after header)
		const keyOffset = magicNumberSize + 4 + headerSize
		const key = dataView.subarray(keyOffset, keyOffset + 32)

		// Extract encrypted content
		const contentOffset = keyOffset + 32
		const encryptedContentSize =
			dataView.byteLength -
			contentOffset -
			binaryDataInfo.reduce((sum, item) => sum + item.size, 0)
		const encryptedContent = dataView.subarray(
			contentOffset,
			contentOffset + encryptedContentSize,
		)

		// Decrypt content
		const decryptedContent = XOR(encryptedContent, key)
		const json = bytesToUtf8(decryptedContent)
		const styleInfo = JSON.parse(json)

		// Extract binary data
		let binaryOffset = contentOffset + encryptedContentSize
		const binaryDataMap = new Map<
			string,
			{ type: "float32" | "uint8"; arrayBuffer: ArrayBuffer }
		>()

		for (const item of binaryDataInfo) {
			const binaryData = dataView.buffer.slice(binaryOffset, binaryOffset + item.size)
			binaryDataMap.set(item.path, { type: item.type, arrayBuffer: binaryData })
			binaryOffset += item.size
		}

		// Create style record with dates
		const styleWithoutBinary: StyleRecord = {
			...styleInfo,
			createdAt: new Date(styleInfo.createdAt),
			updatedAt: new Date(styleInfo.updatedAt),
			originalCreatedAt: new Date(styleInfo.originalCreatedAt),
			referenceCount: Object.keys(styleInfo.referenceImages).length,
		}

		// Restore binary data
		const style = restoreBinaryData(styleWithoutBinary, binaryDataMap)
		return style
	} else {
		// Process old format (original implementation)
		return decodeStyleOld(buffer)
	}
}

function decodeStyleOld(buffer: ArrayBuffer): StyleRecord {
	const dataView = new Uint8Array(buffer)

	// extract key
	const key = dataView.subarray(0, 32)

	// extract content
	const content = dataView.subarray(32)

	// decrypt content
	const decryptedContent = XOR(content, key)

	// convert to string
	const json = bytesToUtf8(decryptedContent)

	const styleInfo = JSON.parse(json)
	const style: StyleRecord = {
		...styleInfo,
		createdAt: new Date(styleInfo.createdAt),
		updatedAt: new Date(styleInfo.updatedAt),
		originalCreatedAt: new Date(styleInfo.originalCreatedAt),
		referenceCount: Object.keys(styleInfo.referenceImages).length,
	}

	// Convert object to typed arrays since style records are stringified without special handling of typed arrays
	for (const imageId in style.referenceImages) {
		const record = style.referenceImages[imageId]

		// Handle deprecated deepCluster feature
		if (record?.features?.deepCluster?.deepClusterFeature) {
			if (!(record.features.deepCluster.deepClusterFeature instanceof Float32Array)) {
				if (Array.isArray(record.features.deepCluster.deepClusterFeature)) {
					record.features.deepCluster.deepClusterFeature = new Float32Array(
						record.features.deepCluster.deepClusterFeature,
					)
				} else {
					record.features.deepCluster.deepClusterFeature = new Float32Array(
						Object.values(record.features.deepCluster.deepClusterFeature),
					)
				}
			}
		}

		// Handle scene deepCluster feature
		if (record?.features?.scene?.clusterFeature) {
			if (!(record.features.scene.clusterFeature instanceof Float32Array)) {
				if (Array.isArray(record.features.scene.clusterFeature)) {
					record.features.scene.clusterFeature = new Float32Array(
						record.features.scene.clusterFeature,
					)
				} else {
					record.features.scene.clusterFeature = new Float32Array(
						Object.values(record.features.scene.clusterFeature),
					)
				}
			}
		}
	}

	return style
}
